import { useState } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { addDays, formatDateLabel, todayISO, toISODate } from '@/lib/date'
import { usePortfolioDates } from '@/hooks/usePicks'

const WEEKDAY_LABELS = ['L', 'M', 'X', 'J', 'V', 'S', 'D']

/** Sprint "Cierre quirúrgico UI/Histórico" (25/09/2026, Sección 2 del encargo): calendario
 * real -- navega mes/año, permite seleccionar directamente cualquier fecha, y marca con un
 * punto los días con publicación oficial real (`usePortfolioDates`, nunca inventado). Nunca
 * recalcula nada -- solo cambia qué `date` pide `usePicks`, que ya devuelve la publicación tal
 * cual quedó persistida ese día. Reutiliza el mismo patrón visual de bottom-sheet que
 * `PickDetailSheet`/`SameGameParlayCard`, sin introducir un concepto de UI nuevo. */
function CalendarSheet({
  date,
  onSelect,
  onClose,
}: {
  date: string
  onSelect: (next: string) => void
  onClose: () => void
}) {
  const initial = new Date(`${date}T12:00:00`)
  const [viewYear, setViewYear] = useState(initial.getFullYear())
  const [viewMonth, setViewMonth] = useState(initial.getMonth()) // 0-11

  const today = todayISO()
  const { data } = usePortfolioDates(viewYear, viewMonth + 1, true)
  const publishedDates = new Set(data?.dates ?? [])

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString('es-ES', {
    month: 'long',
    year: 'numeric',
  })

  const firstOfMonth = new Date(viewYear, viewMonth, 1)
  // Lunes = 0 ... Domingo = 6 (semana empezando en lunes, convención es-ES).
  const leadingBlanks = (firstOfMonth.getDay() + 6) % 7
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()

  const cells: (number | null)[] = [...Array(leadingBlanks).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)]

  const changeMonth = (delta: number) => {
    const d = new Date(viewYear, viewMonth + delta, 1)
    setViewYear(d.getFullYear())
    setViewMonth(d.getMonth())
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-panel rounded-t-3xl border-t border-border max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-panel border-b border-border px-5 py-3.5 flex items-center justify-between z-10">
          <p className="text-xs font-black uppercase tracking-wider text-text">Elegir fecha</p>
          <button onClick={onClose} aria-label="Cerrar" className="p-1.5 rounded-xl bg-panel-elevated text-text-dim hover:text-text">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => changeMonth(-1)}
              aria-label="Mes anterior"
              className="p-1.5 rounded-lg text-text-muted active:bg-panel-elevated"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-bold capitalize">{monthLabel}</span>
            <button
              onClick={() => changeMonth(1)}
              aria-label="Mes siguiente"
              className="p-1.5 rounded-lg text-text-muted active:bg-panel-elevated"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {WEEKDAY_LABELS.map((w) => (
              <span key={w} className="text-[9px] font-bold uppercase text-text-dim py-1">
                {w}
              </span>
            ))}
            {cells.map((day, i) => {
              if (day == null) return <span key={`b${i}`} />
              const iso = toISODate(new Date(viewYear, viewMonth, day))
              const isSelected = iso === date
              const isToday = iso === today
              const isFuture = iso > today
              const hasPublication = publishedDates.has(iso)
              return (
                <button
                  key={iso}
                  disabled={isFuture}
                  onClick={() => {
                    onSelect(iso)
                    onClose()
                  }}
                  className={`relative h-9 rounded-xl text-xs font-bold transition ${
                    isFuture
                      ? 'text-text-dim/30 cursor-not-allowed'
                      : isSelected
                      ? 'bg-brand text-black'
                      : isToday
                      ? 'border border-brand/50 text-brand'
                      : 'text-text hover:bg-panel-elevated'
                  }`}
                >
                  {day}
                  {hasPublication && !isFuture && (
                    <span
                      className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full ${
                        isSelected ? 'bg-black' : 'bg-brand'
                      }`}
                    />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

/** Navegación de un día a la vez — coincide con como `target_date` funciona hoy en
 * `/api/mini-app/picks` y `/parlays` (una fecha puntual, no un rango). Tocar la fecha central
 * abre el calendario (Sección 2 del encargo, 25/09/2026); `‹ ›` de día anterior/siguiente se
 * mantienen sin cambios. */
export function DateNav({ date, onChange }: { date: string; onChange: (next: string) => void }) {
  const isToday = date === todayISO()
  const [showCalendar, setShowCalendar] = useState(false)

  return (
    <div className="flex items-center justify-between gap-2 card py-2">
      <button
        onClick={() => onChange(addDays(date, -1))}
        className="p-1.5 rounded-lg text-text-muted active:bg-panel-elevated"
        aria-label="Día anterior"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        onClick={() => setShowCalendar(true)}
        className="text-sm font-semibold capitalize active:opacity-70"
        aria-label="Elegir fecha"
      >
        {formatDateLabel(date)}
      </button>
      <button
        onClick={() => onChange(addDays(date, 1))}
        disabled={isToday}
        className="p-1.5 rounded-lg text-text-muted disabled:opacity-30 active:bg-panel-elevated"
        aria-label="Día siguiente"
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      {showCalendar && (
        <CalendarSheet date={date} onSelect={onChange} onClose={() => setShowCalendar(false)} />
      )}
    </div>
  )
}
